import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, ThemeSetting, applyTheme, resolveTheme } from '../utils/theme';

interface ThemeContextType {
  theme: Theme;           // 実際に適用されてるテーマ（light/dark）
  themeSetting: ThemeSetting; // ユーザー設定（auto/light/dark）
  toggleTheme: () => void;
  setTheme: (theme: ThemeSetting) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeSetting, setThemeSetting] = useState<ThemeSetting>(() => {
    if (typeof window === 'undefined') return 'auto';
    return (localStorage.getItem('theme') as ThemeSetting) || 'auto';
  });

  const [theme, setResolvedTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = (localStorage.getItem('theme') as ThemeSetting) || 'auto';
    const resolved = resolveTheme(saved);
    setTimeout(() => applyTheme(resolved), 0);
    return resolved;
  });

  // テーマ設定変更時に適用
  useEffect(() => {
    const resolved = resolveTheme(themeSetting);
    setResolvedTheme(resolved);
    applyTheme(resolved);
    localStorage.setItem('theme', themeSetting);
  }, [themeSetting]);

  // auto モードの場合、OS設定変更を監視
  useEffect(() => {
    if (themeSetting !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(newTheme);
      applyTheme(newTheme);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [themeSetting]);

  // auto → light → dark のサイクル
  const toggleTheme = () => {
    setThemeSetting(prev => {
      if (prev === 'auto') return 'light';
      if (prev === 'light') return 'dark';
      return 'auto';
    });
  };

  const setTheme = (newSetting: ThemeSetting) => {
    setThemeSetting(newSetting);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeSetting, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
